import classnames from 'classnames';
import { FC, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useUpdateCustomerMutation } from '../../api/queries/ticket';
import AddressData from '../../components/AddressData/AddressData';
import Alert from '../../components/Alert/Alert';
import PersonData from '../../components/PersonData/PersonData';
import { Separator } from '../../components/Separator/Separator';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { TicketContext } from '../../contexts/TicketContext';
import { isTicketCancellable } from '../../helpers/TicketHelpers';
import { Customer as CustomerType } from '../../interfaces/Customer';
import { TicketRoutePaths } from '../../interfaces/Routes';
import { Ticket } from '../../interfaces/Ticket';
import { sendSubmitPersonalDetailsClicked } from '../../services/Analytics';
import usePageTitle from '../../services/hooks/usePageTitle';
import StepsService from '../../services/StepsService';
import AcceptData from './AcceptData/AcceptData';
import style from './Customer.module.scss';
import CustomerController from './CustomerController';
import CustomerHeader from './CustomerHeader';

const Customer: FC = () => {
  const { stylesheet } = useContext(StylesheetContext);
  const { setTicket, customer, setCustomer, ticket } = useContext(TicketContext);
  const { updateCustomer, isPending, isSuccess, isError } = useUpdateCustomerMutation();
  const [isCustomerFormTouched, setIsCustomerFormTouched] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState(CustomerController.getInitialFieldErrors());

  const navigate = useNavigate();

  const { t } = useTranslation();
  const classNamesDetails = classnames('row', style.details);

  usePageTitle(t('pageTitle:customer'));

  const onFieldChange = useCallback(<T extends keyof CustomerType, >(value: CustomerType[T], fieldName: T ): void => {
    if (fieldErrors[fieldName]) {
      fieldErrors[fieldName] = undefined;
    }

    setIsCustomerFormTouched(true);
    CustomerController.onFieldChange({ value, fieldName, customer, setCustomer });
  }, [customer, setCustomer, fieldErrors]);

  const onSave = useCallback(async (ticket: Ticket): Promise<boolean> => {
    const isValid = CustomerController.validateFields(customer, setFieldErrors, t);

    if (!isValid) {
      return false;
    }

    sendSubmitPersonalDetailsClicked();

    if (!isCustomerFormTouched) {
      return true;
    }

    if (!customer) {
      return false;
    }

    const updatedCustomer = await updateCustomer({ ...customer, hashId: ticket.hashId });
    setTicket({ ...ticket, customer: updatedCustomer });

    return true;
  }, [customer, t, isCustomerFormTouched, setTicket, updateCustomer]);

  if (!ticket) {
    // Should never happen, ErrorElement will be rendered instead
    return null;
  }

  const canCancelTicket = isTicketCancellable(ticket, stylesheet);

  return (
    <div>
      <CustomerHeader allowCancelling={canCancelTicket} stylesheet={stylesheet} />

      <div className={classNamesDetails}>
        {!!customer && (
          <PersonData
            customer={customer}
            fieldErrors={fieldErrors}
            isLoading={isPending}
            onFieldChange={onFieldChange}
            saveStatus={{ success: isSuccess, error: isError }}
            stylesheet={stylesheet}
          />
        )}
        {!!customer && (
          <AddressData
            allowCancelling={canCancelTicket}
            customer={customer}
            hashId={ticket.hashId}
            stylesheet={stylesheet}
          />
        )}
      </div>

      <AcceptData
        isLoading={isPending}
        onSave={() => onSave(ticket)}
      />

      {canCancelTicket && (
        <div className={style.mobileOnly}>
          <Alert
            className={style.cancellation}
            level="info"
            text={t('alert:cancelTicket')}
            textButton={{
              text: t('alert:clickToCancel'),
              onClick: () => navigate(StepsService.getStepUri(ticket.hashId, TicketRoutePaths.cancel)),
            }}
          />
        </div>
      )}

      <Separator classes={classnames(style.separator)}/>
    </div>
  );
};

export default Customer;
